import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logoImg from '../../logo.png';
import {
  Trophy, Play, ShoppingBag, ClipboardList, ChevronRight,
  Star, Users, Calendar, MapPin, Eye, Zap, Shield, Award
} from 'lucide-react';

// ── Data snapshots ────────────────────────────────────────────────────────────
const UPCOMING_TOURNAMENTS = [
  { id: 1, name: 'Summer Showdown 2026', dates: 'Jun 14–15', location: 'Zions Bank Stadium, Herriman', ageGroups: ['U10','U12','U14','U16'], spots: 3, status: 'open' },
  { id: 2, name: 'Fall Cup Classic',     dates: 'Sep 12',    location: 'Provo Canyon Sports Park', ageGroups: ['U8','U10','U12'], spots: 6, status: 'open' },
  { id: 3, name: 'Winter Invitational',  dates: 'Dec 5–6',   location: 'Utah Indoor Complex', ageGroups: ['U12','U14','U16','U19'], spots: 2, status: 'upcoming' },
];

const FEATURED_VIDEOS = [
  { id: 'v1', title: 'Summer Classic 2025 — U12 Championship', category: 'Tournament', views: 842,  duration: '12:34', team: 'Thunder FC' },
  { id: 'v2', title: 'Lightning Squad — Best Goals of Spring',  category: 'Highlights', views: 1243, duration: '6:18',  team: 'Lightning Squad' },
  { id: 'v6', title: 'Blue Wave — Tournament Champions 2024',   category: 'Tournament', views: 1891, duration: '9:22',  team: 'Blue Wave' },
];

const FEATURED_GEAR = [
  { id: 1, name: 'UC Elites Jersey',   price: 65,  emoji: '👕', tag: 'Best Seller' },
  { id: 2, name: 'UC Elites Hoodie',   price: 55,  emoji: '🧥', tag: 'Fan Fave' },
  { id: 3, name: 'Backpack / Gear Bag',price: 75,  emoji: '🎒', tag: 'New' },
  { id: 4, name: 'UC Elites Cap',      price: 32,  emoji: '🧢', tag: null },
];

const STATS = [
  { value: '8+',  label: 'Teams',       icon: Users },
  { value: '50+', label: 'Players',     icon: Shield },
  { value: '6',   label: 'Tournaments', icon: Trophy },
  { value: '100%', label: 'Heart',      icon: Zap },
];

// ── Marquee ticker ────────────────────────────────────────────────────────────
const TICKER_ITEMS = [
  '🏆 Summer Showdown 2026 — Registration NOW OPEN',
  '⚡ UC Elites U12 Thunder FC — Spring Champions',
  '🎯 7v7 Football — Utah',
  '🏅 Fall Cup Classic — Sep 12 at Provo Canyon Sports Park',
  '🔥 New gear just dropped in the Team Store',
  '📋 Register your team for the 2026 season',
];

// ── Gold gradient text helper ─────────────────────────────────────────────────
const goldText = { color: '#c6a35a' };

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 50);
  }, []);

  const handlePortalClick = () => {
    if (user) navigate('/dashboard');
    else navigate('/login');
  };

  return (
    <div style={{ fontFamily: "'Barlow', system-ui, sans-serif", background: '#0a0a0a', color: '#ffffff', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── NAV ─────────────────────────────────────────────────────────────── */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, background: 'rgba(0,0,0,0.96)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(198,163,90,0.16)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1.5rem', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img src={logoImg} alt="UC Elites" style={{ width: 54, height: 54, objectFit: 'contain' }} />
            <span style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 30, letterSpacing: '0.08em', lineHeight: 0.95, textTransform: 'uppercase', ...goldText }}>UC ELITES</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {[
              { label: 'Tournaments', href: '#tournaments' },
              { label: 'Highlights',  href: '#highlights' },
              { label: 'Store',       href: '#store' },
            ].map(link => (
              <a key={link.href} href={link.href}
                style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)', textDecoration: 'none', borderRadius: 6, transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#c6a35a'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.75)'}>
                {link.label}
              </a>
            ))}
            <button onClick={handlePortalClick}
              style={{ marginLeft: 8, padding: '8px 20px', background: '#c6a35a', color: '#050505', fontWeight: 800, fontSize: 13, border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: "'Barlow', sans-serif", letterSpacing: '0.04em' }}>
              {user ? 'Dashboard' : 'Sign In'}
            </button>
          </div>
        </div>
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', paddingTop: 64, overflow: 'hidden' }}>
        {/* Background layers */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 72px), repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 72px)' }} />

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', padding: '2rem 1.5rem', maxWidth: 900, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease' }}>
          {/* Badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(198,163,90,0.20)', borderRadius: 100, padding: '6px 16px', marginBottom: '1.5rem' }}>
            <Zap size={13} color="#c6a35a" />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#c6a35a', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>Utah's Premier 7v7 Organization</span>
          </div>

          {/* Logo mark */}
          <div style={{ marginBottom: '1rem' }}>
            <img src={logoImg} alt="UC Elites" style={{ width: 146, height: 146, objectFit: 'contain' }} />
          </div>

          {/* Headline */}
          <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(52px, 9vw, 96px)', fontWeight: 900, lineHeight: 0.9, letterSpacing: '-0.01em', marginBottom: '1.25rem' }}>
            <span style={{ display: 'block', color: '#ffffff' }}>BUILT FOR</span>
            <span style={{ display: 'block', ...goldText }}>CHAMPIONS</span>
          </h1>

          <p style={{ fontSize: 'clamp(16px, 2.2vw, 20px)', color: 'rgba(255,255,255,0.65)', maxWidth: 560, margin: '0 auto 2.5rem', lineHeight: 1.6, fontWeight: 400 }}>
            Elite 7v7 football development, competitive tournaments, and a community that pushes every player to their peak.
          </p>

          {/* CTAs */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => document.getElementById('register-section').scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '14px 32px', background: '#c6a35a', color: '#050505', fontWeight: 900, fontSize: 15, border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#d1b06a'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#c6a35a'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              Register Now
            </button>
            <button onClick={() => document.getElementById('tournaments').scrollIntoView({ behavior: 'smooth' })}
              style={{ padding: '14px 32px', background: 'transparent', color: '#ffffff', fontWeight: 700, fontSize: 15, border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#c6a35a'; e.currentTarget.style.color = '#c6a35a'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; e.currentTarget.style.color = '#ffffff'; }}>
              View Tournaments
            </button>
          </div>
        </div>

        {/* Scroll cue */}
        <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, opacity: 0.5 }}>
          <span style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#c6a35a' }}>Scroll</span>
          <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, rgba(255,255,255,0.5), transparent)' }} />
        </div>
      </section>

      {/* ── TICKER ──────────────────────────────────────────────────────────── */}
      <div style={{ background: '#060606', overflow: 'hidden', padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.10)', borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
        <div style={{ display: 'flex', gap: '4rem', animation: 'tickerScroll 30s linear infinite', whiteSpace: 'nowrap' }}>
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} style={{ fontSize: 13, fontWeight: 800, color: '#ffffff', letterSpacing: '0.05em', fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase', flexShrink: 0 }}>{item}</span>
          ))}
        </div>
        <style>{`@keyframes tickerScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
      </div>

      {/* ── STATS BAR ───────────────────────────────────────────────────────── */}
      <section style={{ background: '#0d0d0d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 1.5rem', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ padding: '2rem 1rem', textAlign: 'center', borderRight: i < 3 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
              <div style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 900, fontFamily: "'Barlow Condensed', sans-serif", color: '#ffffff', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 6, fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TOURNAMENTS ─────────────────────────────────────────────────────── */}
      <section id="tournaments" style={{ padding: '6rem 1.5rem', background: '#0a0a0a' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeader icon={<Trophy size={22} color="#c6a35a" />} label="Tournaments" title="Compete at the Highest Level" sub="Open registration for upcoming 7v7 tournaments across all age groups." />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginTop: '3rem' }}>
            {UPCOMING_TOURNAMENTS.map((t, i) => (
              <div key={t.id}
                style={{ background: '#101010', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, overflow: 'hidden', transition: 'transform 0.2s, border-color 0.2s', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'rgba(198,163,90,0.30)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                onClick={() => handlePortalClick()}>
                {/* Card top accent */}
                <div style={{ height: 2, background: '#c6a35a' }} />
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                    <h3 style={{ fontSize: 18, fontWeight: 800, color: '#ffffff', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em', lineHeight: 1.2 }}>{t.name}</h3>
                    <span style={{ fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 100, background: t.status === 'open' ? 'rgba(255,255,255,0.04)' : 'rgba(198,163,90,0.08)', color: t.status === 'open' ? '#ffffff' : '#c6a35a', border: `1px solid ${t.status === 'open' ? 'rgba(255,255,255,0.12)' : 'rgba(198,163,90,0.20)'}`, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', marginLeft: 8 }}>{t.status === 'open' ? 'Open' : 'Coming Soon'}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                      <Calendar size={13} color="#c6a35a" /><span>{t.dates}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                      <MapPin size={13} color="#c6a35a" /><span>{t.location}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                    {t.ageGroups.map(ag => (
                      <span key={ag} style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', background: 'rgba(255,255,255,0.04)', color: '#ffffff', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, fontFamily: "'Barlow Condensed', sans-serif" }}>{ag}</span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{t.spots} spots remaining</span>
                    <button style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800, color: '#c6a35a', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      Register <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <button onClick={handlePortalClick}
              style={{ padding: '12px 32px', background: 'transparent', color: '#c6a35a', fontWeight: 700, fontSize: 14, border: '1px solid rgba(198,163,90,0.22)', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              View All Tournaments →
            </button>
          </div>
        </div>
      </section>

      {/* ── HIGHLIGHTS ──────────────────────────────────────────────────────── */}
      <section id="highlights" style={{ padding: '6rem 1.5rem', background: '#111111' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeader icon={<Play size={22} color="#c6a35a" />} label="Highlights" title="Watch the Action" sub="Tournament recaps, top plays, and full match replays from the field." />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20, marginTop: '3rem' }}>
            {FEATURED_VIDEOS.map(v => (
              <div key={v.id}
                style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s, border-color 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'rgba(198,163,90,0.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                onClick={handlePortalClick}>
                {/* Thumbnail placeholder */}
                <div style={{ aspectRatio: '16/9', background: 'linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(0,0,0,0.75)', border: '1px solid rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                    <Play size={22} color="#ffffff" style={{ marginLeft: 3 }} />
                  </div>
                  <span style={{ position: 'absolute', bottom: 8, right: 10, fontSize: 11, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.75)', padding: '2px 8px', borderRadius: 4 }}>{v.duration}</span>
                  <span style={{ position: 'absolute', top: 10, left: 10, fontSize: 10, fontWeight: 800, color: '#050505', background: '#c6a35a', padding: '2px 8px', borderRadius: 4, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>{v.category}</span>
                </div>
                <div style={{ padding: '1rem' }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#ffffff', lineHeight: 1.35, marginBottom: 6 }}>{v.title}</h3>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{v.team}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                      <Eye size={12} />{v.views.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <button onClick={handlePortalClick}
              style={{ padding: '12px 32px', background: 'transparent', color: '#c6a35a', fontWeight: 700, fontSize: 14, border: '1px solid rgba(198,163,90,0.22)', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              Full Video Library →
            </button>
          </div>
        </div>
      </section>

      {/* ── REGISTRATION CTA ────────────────────────────────────────────────── */}
      <section id="register-section" style={{ padding: '6rem 1.5rem', background: '#0a0a0a', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Gold border card */}
          <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 24, padding: 'clamp(2rem, 5vw, 4rem)', textAlign: 'center', background: '#0b0b0b', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: '#c6a35a' }} />
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(198,163,90,0.20)', borderRadius: 100, padding: '5px 14px', marginBottom: '1.5rem' }}>
              <ClipboardList size={13} color="#c6a35a" />
              <span style={{ fontSize: 11, fontWeight: 800, color: '#c6a35a', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>Registration Portal</span>
            </div>
            <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 900, lineHeight: 1, marginBottom: '1rem' }}>
              <span style={{ color: '#ffffff' }}>READY TO </span>
              <span style={{ ...goldText }}>RUN IT?</span>
            </h2>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', maxWidth: 500, margin: '0 auto 2rem', lineHeight: 1.6 }}>
              Register your team for the 2026 season. Secure your spot in tournaments, access team management tools, and join the UC Elites family.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={handlePortalClick}
                style={{ padding: '14px 36px', background: '#c6a35a', color: '#050505', fontWeight: 900, fontSize: 15, border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = '#d1b06a'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = '#c6a35a'; }}>
                Register Your Team
              </button>
              <button onClick={handlePortalClick}
                style={{ padding: '14px 36px', background: 'rgba(255,255,255,0.06)', color: '#ffffff', fontWeight: 700, fontSize: 15, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}>
                Parent Portal
              </button>
            </div>
            {/* Quick info row */}
            <div style={{ display: 'flex', gap: '2rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '2.5rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              {[
                { icon: <Users size={15} />, label: 'Ages U8 – U19' },
                { icon: <Trophy size={15} />, label: '6 Tournaments / Year' },
                { icon: <Shield size={15} />, label: 'Coach + Parent Portals' },
                { icon: <Award size={15} />, label: 'Competitive & Development' },
              ].map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                  <span style={{ color: '#c6a35a' }}>{f.icon}</span>
                  <span>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM STORE ──────────────────────────────────────────────────────── */}
      <section id="store" style={{ padding: '6rem 1.5rem', background: '#111111' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <SectionHeader icon={<ShoppingBag size={22} color="#c6a35a" />} label="Team Store" title="Rep the Crest" sub="Official UC Elites gear — jerseys, hoodies, bags, and more." />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16, marginTop: '3rem' }}>
            {FEATURED_GEAR.map(item => (
              <div key={item.id}
                style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s', position: 'relative' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'rgba(198,163,90,0.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                onClick={handlePortalClick}>
                {item.tag && (
                  <span style={{ position: 'absolute', top: 12, right: 12, fontSize: 9, fontWeight: 800, padding: '2px 8px', background: '#c6a35a', color: '#050505', borderRadius: 100, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>{item.tag}</span>
                )}
                <div style={{ fontSize: 48, marginBottom: 12, textAlign: 'center' }}>{item.emoji}</div>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: '#ffffff', marginBottom: 6, fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.02em' }}>{item.name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                  <span style={{ fontSize: 20, fontWeight: 900, color: '#ffffff', fontFamily: "'Barlow Condensed', sans-serif" }}>${item.price}</span>
                  <button style={{ fontSize: 11, fontWeight: 800, color: '#050505', background: '#c6a35a', border: 'none', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.04em' }}>Shop</button>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <button onClick={handlePortalClick}
              style={{ padding: '12px 32px', background: '#c6a35a', color: '#050505', fontWeight: 800, fontSize: 14, border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = '#d1b06a'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.background = '#c6a35a'; }}>
              Shop Full Collection →
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer style={{ background: '#000000', borderTop: '1px solid rgba(255,255,255,0.10)', padding: '3rem 1.5rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '2rem', marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <img src={logoImg} alt="UC Elites" style={{ width: 56, height: 56, objectFit: 'contain' }} />
              <div>
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 28, letterSpacing: '0.08em', lineHeight: 0.95, textTransform: 'uppercase', ...goldText }}>UC ELITES</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Utah 7v7</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {[['Tournaments','#tournaments'],['Highlights','#highlights'],['Store','#store'],['Register','#register-section']].map(([label, href]) => (
                <a key={href} href={href} style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#c6a35a'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}>
                  {label}
                </a>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>© 2026 UC Elites. All rights reserved.</span>
            <button onClick={handlePortalClick}
              style={{ fontSize: 12, fontWeight: 700, color: '#c6a35a', background: 'none', border: '1px solid rgba(198,163,90,0.20)', padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {user ? 'Go to Dashboard' : 'Team Login'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ── Reusable section header ───────────────────────────────────────────────────
function SectionHeader({ icon, label, title, sub }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(198,163,90,0.18)', borderRadius: 100, padding: '5px 14px', marginBottom: '1rem' }}>
        {icon}
        <span style={{ fontSize: 11, fontWeight: 800, color: '#c6a35a', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Barlow Condensed', sans-serif" }}>{label}</span>
      </div>
      <h2 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 900, color: '#ffffff', marginBottom: '0.75rem', lineHeight: 1.1 }}>{title}</h2>
      <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>{sub}</p>
    </div>
  );
}
